import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, toApiError, type ApiErrorShape } from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import type { components } from '@/api/schema';

type JobStatus = components['schemas']['JobStatusResponse'];
type JobType = components['schemas']['JobType'];
type LayoutTypeString = components['schemas']['LayoutTypeString'];
type Rating = components['schemas']['Rating'];
type ReadingListResponse = components['schemas']['ReadingListResponse'];
type ReadingDetail = components['schemas']['ReadingDetailResponse'];

export interface CreateJobInput {
  jobType: JobType;
  layoutType: LayoutTypeString;
  userQuestion?: string;
  image?: File | null;
  idempotencyKey?: string;
}

function assertNoError<T>(
  data: T | undefined,
  err: unknown,
  status: number,
): asserts data is T {
  if (err !== undefined || data === undefined) {
    throw toApiError(err, status);
  }
}

// Large multipart uploads over a slow/flaky mobile connection can otherwise hang
// indefinitely (fetch() has no built-in timeout), leaving the UI stuck on
// "Отправляем..." forever with no error to react to.
export const CREATE_JOB_TIMEOUT_MS = 60_000;

export async function createReadingJob({
  jobType,
  layoutType,
  userQuestion,
  image,
  idempotencyKey,
}: CreateJobInput): Promise<
  components['schemas']['CreateJobAcceptedResponse']
> {
  const body: Record<string, unknown> = { jobType, layoutType };
  if (userQuestion) body.userQuestion = userQuestion;
  if (image) body.image = image;

  const key =
    idempotencyKey ?? globalThis.crypto?.randomUUID?.() ?? String(Date.now());

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CREATE_JOB_TIMEOUT_MS);
  try {
    // openapi-fetch serializes `multipart/form-data` bodies automatically when
    // any field is a Blob/File. The `idempotency-key` header matches the spec.
    const { data, error, response } = await api.POST('/api/v1/readings/jobs', {
      params: { header: { 'idempotency-key': key } },
      body: body as never,
      signal: controller.signal,
      bodySerializer(payload) {
        const fd = new FormData();
        for (const [k, v] of Object.entries(
          payload as Record<string, unknown>,
        )) {
          if (v === undefined || v === null) continue;
          if (v instanceof Blob) fd.append(k, v);
          else fd.append(k, String(v));
        }
        return fd;
      },
    });
    assertNoError(data, error, response.status);
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

export function useCreateJob() {
  return useMutation<
    components['schemas']['CreateJobAcceptedResponse'],
    ApiErrorShape,
    CreateJobInput
  >({
    mutationFn: createReadingJob,
  });
}

export const JOB_POLL_INTERVAL_MS = 2000;
export const JOB_POLL_SLOW_INTERVAL_MS = 5000;
// Photo readings take 1.5–2 min (vision analysis + interpretation), so there is
// nothing to find early on. Polling slowly for the first minute cuts requests
// without delaying the moment the finished result is picked up.
export const JOB_POLL_SLOW_PHASE_MS = 60_000;

/**
 * Next poll delay for a reading job, or `false` once it reached a final state.
 * `fetchCount` is the number of successful fetches so far; the first one fires
 * on mount, so fetch N happens roughly (N - 1) intervals in.
 */
export function getJobPollInterval(
  data: JobStatus | undefined,
  fetchCount: number,
): number | false {
  if (data?.status === 'completed' || data?.status === 'failed') return false;
  const inSlowPhase =
    data?.jobType === 'PHOTO_READING' &&
    (fetchCount - 1) * JOB_POLL_SLOW_INTERVAL_MS < JOB_POLL_SLOW_PHASE_MS;
  return inSlowPhase ? JOB_POLL_SLOW_INTERVAL_MS : JOB_POLL_INTERVAL_MS;
}

// Giving up on a status poll drops the user off the loading screen while the
// reading keeps running, so transient failures are retried for ~2.5 min —
// long enough to outlast a full 60 s throttler block or a mobile network drop.
export const JOB_POLL_MAX_RETRIES = 8;
export const JOB_POLL_MAX_RETRY_DELAY_MS = 30_000;

/**
 * Rate limiting, server errors and network failures (which reach the query as
 * a raw `TypeError` with no status) are worth retrying; 401/404 are not.
 */
export function shouldRetryJobPoll(
  failureCount: number,
  error: unknown,
): boolean {
  const status = (error as Partial<ApiErrorShape> | null)?.status;
  const transient =
    status === undefined || status === 0 || status === 429 || status >= 500;
  return transient && failureCount < JOB_POLL_MAX_RETRIES;
}

/** Exponential backoff from the fast poll interval: 2, 4, 8, 16, 30, 30… s. */
export function getJobPollRetryDelay(failureCount: number): number {
  return Math.min(
    JOB_POLL_INTERVAL_MS * 2 ** failureCount,
    JOB_POLL_MAX_RETRY_DELAY_MS,
  );
}

export function useJobStatus(jobId: string | null) {
  return useQuery<JobStatus, ApiErrorShape>({
    queryKey: queryKeys.readingJob(jobId ?? ''),
    enabled: !!jobId,
    refetchInterval: (query) =>
      getJobPollInterval(query.state.data, query.state.dataUpdateCount),
    retry: shouldRetryJobPoll,
    retryDelay: getJobPollRetryDelay,
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        '/api/v1/readings/jobs/{jobId}',
        {
          params: { path: { jobId: jobId! } },
        },
      );
      assertNoError(data, error, response.status);
      return data;
    },
  });
}

export function useReading(readingId: number | null) {
  return useQuery<ReadingDetail, ApiErrorShape>({
    queryKey: queryKeys.reading(readingId ?? -1),
    enabled: readingId !== null && readingId >= 0,
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        '/api/v1/readings/{readingId}',
        {
          params: { path: { readingId: readingId! } },
        },
      );
      assertNoError(data, error, response.status);
      return data;
    },
  });
}

export interface ReadingsListParams {
  limit?: number;
}

export function useReadingsList({ limit = 20 }: ReadingsListParams = {}) {
  return useInfiniteQuery<ReadingListResponse, ApiErrorShape>({
    queryKey: queryKeys.readingsList(limit),
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error, response } = await api.GET('/api/v1/readings', {
        params: {
          query: {
            ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
            limit: String(limit),
          },
        },
      });
      assertNoError(data, error, response.status);
      return data;
    },
    getNextPageParam: (last) =>
      last.hasMore ? (last.nextCursor ?? undefined) : undefined,
  });
}

export interface RateReadingInput {
  readingId: number;
  rating: Rating;
}

export function useRateReading() {
  const queryClient = useQueryClient();
  return useMutation<
    components['schemas']['OkResponse'],
    ApiErrorShape,
    RateReadingInput
  >({
    mutationFn: async ({ readingId, rating }) => {
      const { data, error, response } = await api.POST(
        '/api/v1/readings/{readingId}/rate',
        {
          params: { path: { readingId } },
          body: { rating },
        },
      );
      assertNoError(data, error, response.status);
      return data;
    },
    onSuccess: (_ok, { readingId, rating }) => {
      queryClient.setQueryData<ReadingDetail>(
        queryKeys.reading(readingId),
        (prev) => (prev ? { ...prev, rating } : prev),
      );
      queryClient.invalidateQueries({ queryKey: ['readings', 'list'] });
    },
  });
}
